import React, { useRef, useState, useEffect } from 'react';
import { FaChevronRight } from "react-icons/fa";
import { IoIosAdd } from "react-icons/io";
import { IoChevronDownSharp } from "react-icons/io5";
import { MdEdit } from "react-icons/md";
import { baseUrl } from '../config';



const Calenders = ({ column, tasks, setTasks }) => {
  const [dropdownVisible, setDropdownVisible] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    dueDate: '',
    completion: 0,
    assignedTo: '',
    column: column,
  });
  const [draggingIndex, setDraggingIndex] = useState(null);
  const progressRef = useRef({});

  const fetchTasks = async () => {
    const response = await fetch(`/api/progresstracker/tasks?column=${column}`);
    const data = await response.json();
    setTasks(data);
  };

  useEffect(() => {
    fetchTasks();
  }, [column]);

  const handleDragStart = (e, taskIndex) => {
    e.dataTransfer.setData('taskIndex', taskIndex);
    e.dataTransfer.setData('fromColumn', column);
  };

  const handleDrop = (e) => {
    const taskIndex = e.dataTransfer.getData('taskIndex');
    const fromColumn = e.dataTransfer.getData('fromColumn');
    
    if (fromColumn !== column) {
      handleColumnChange(taskIndex, column);
    }
    e.preventDefault();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleColumnChange = async (taskIndex, newColumn) => {
    const updatedTask = { ...tasks[taskIndex], column: newColumn };
    const response = await fetch(`/api/progresstracker/tasks/${updatedTask.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updatedTask),
    });
    if (response.ok) {
      const updatedTasks = tasks.map((task, index) =>
        index === taskIndex ? updatedTask : task
      );
      setTasks(updatedTasks);
    }
    setDropdownVisible({});
  };

  const toggleDropdown = (index) => {
    setDropdownVisible((prevState) => ({
      ...prevState,
      [index]: !prevState[index],
    }));
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setNewTask((prevTask) => ({
      ...prevTask,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isEditing && editIndex !== null) {
      const updatedTask = { ...newTask, id: tasks[editIndex].id };
      const response = await fetch(`/api/progresstracker/tasks/${updatedTask.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedTask),
      });
      if (response.ok) {
        const data = await response.json();
        const updatedTasks = tasks.map((task, index) =>
          index === editIndex ? data : task
        );
        setTasks(updatedTasks);
      }
    } else {
      const response = await fetch('/api/progresstracker/tasks', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newTask),
      });
      if (response.ok) {
        const data = await response.json();
        setTasks((prevTasks) => [...prevTasks, data]);
      }
    }
    resetForm();
  };

  const resetForm = () => {
    setNewTask({
      title: '',
      description: '',
      dueDate: '',
      completion: 0,
      assignedTo: '',
      column: column,
    });
    setShowForm(false);
    setIsEditing(false);
    setEditIndex(null);
  };

  const handleEditTask = (task, originalIndex) => {
    setIsEditing(true);
    setEditIndex(originalIndex);
    setNewTask(task);
    setShowForm(true);
  };

  return (
    <div
      className="bg-white p-4 rounded-lg w-25 h-full mx-2 shadow-sm"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div className='flex justify-between h-10 border-b border-gray-400'>
        <div className='flex'>
          <div className={`w-3 h-3 rounded-full mr-2 ${column === 'New Task' ? 'bg-green-500' : column === 'Ready' ? 'bg-blue-600' : column === 'In Progress' ? 'bg-yellow-600' : column === 'Ready for review' ? 'bg-purple-600' : 'bg-red-600'}`}></div>
          <p className="text-left">{column}</p>
        </div>
        <div className='flex'>
          <IoIosAdd className="text-xl mx-1 cursor-pointer" onClick={() => setShowForm(true)} />
          <FaChevronRight className="text-sm" />
        </div>
      </div>
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-md p-5 w-full max-w-md mx-auto relative">
            <button 
              onClick={resetForm} 
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
            >
              &times;
            </button>
            <form onSubmit={handleSubmit}>
              <input
                type="text"
                name="title"
                value={newTask.title}
                onChange={handleFormChange}
                placeholder="Title"
                required
                className="w-full p-2 mb-2 border border-gray-300 rounded-lg"
              />
              <textarea
                name="description"
                value={newTask.description}
                onChange={handleFormChange}
                placeholder="Description"
                required
                className="w-full p-2 mb-2 border border-gray-300 rounded-lg h-24 resize-none"
              />
              <input
                type="date"
                name="dueDate"
                value={newTask.dueDate}
                onChange={handleFormChange}
                className="w-full p-2 mb-2 border border-gray-300 rounded-lg"
              />
              <input
                type="number"
                name="completion"
                value={newTask.completion}
                onChange={handleFormChange}
                placeholder="Completion (%)"
                min="0"
                max="100"
                className="w-full p-2 mb-2 border border-gray-300 rounded-lg"
              />
              <input
                type="text"
                name="assignedTo"
                value={newTask.assignedTo}
                onChange={handleFormChange}
                placeholder="Assigned To"
                className="w-full p-2 mb-4 border border-gray-300 rounded-lg"
              />
              <div className="flex justify-end gap-2">
                <button type="submit" className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition">
                  {isEditing ? 'Update Task' : 'Add Task'}
                </button>
                <button type="button" onClick={resetForm} className="bg-gray-500 text-white p-2 rounded-lg hover:bg-gray-600 transition mr-2">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {tasks && tasks.length > 0 ? (
        tasks.map((task, index) => (
          <div
            key={task.id}
            className="bg-white p-4 mt-2 rounded-lg shadow-sm relative"
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
          >
            <div className="flex justify-between items-center mb-2">
              <h6 className="font-medium">{task.title}</h6>
              <div className="flex items-center">
                <IoChevronDownSharp
                  className="text-lg cursor-pointer"
                  onClick={() => toggleDropdown(index)}
                />
                <MdEdit
                  className="text-xl mx-1 cursor-pointer"
                  onClick={() => handleEditTask(task, index)}
                />
              </div>
            </div>
            {dropdownVisible[index] && (
              <div
                className="bg-gray-100 border border-gray-200 rounded-lg p-2"
                style={{ display: dropdownVisible[index] ? 'block' : 'none' }}
              >
                <p>{task.description}</p>
                <p>Due Date: {task.dueDate}</p>
                <p>Assigned To: {task.assignedTo}</p>
                <p>Completion: {task.completion}%</p>
              </div>
            )}
          </div>
        ))
      ) : (
        <p className="text-gray-500 text-center">No tasks available</p>
      )}
    </div>
  );
};

export default Calenders;

